/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import styles from "src/components/table/table.module.css";
import {
  Column,
  UsePaginationInstanceProps,
  UseTableInstanceProps,
} from "react-table";
import cn from "classnames";
import { HeaderGroup } from "react-table";
import { Icon } from "src/components/icon/icon";
import { Row } from "react-table";
import "regenerator-runtime"; // Imported to fix useAsyncDebounce: https://github.com/TanStack/table/issues/3962#issuecomment-1136532672
import { useAsyncDebounce } from "react-table";
import { Tooltip } from "src/components/ui-components/Tooltip/Tooltip";
import { TableCell } from "./TableCell";

export interface TableRow<T extends object> {
  original: T;
}

export const TableResults = <T extends object>(props: {
  getTableProps: UseTableInstanceProps<T>["getTableProps"];
  headerGroups: UseTableInstanceProps<T>["headerGroups"];
  getTableBodyProps: UseTableInstanceProps<T>["getTableBodyProps"];
  page: UsePaginationInstanceProps<T>["page"];
  prepareRow: UseTableInstanceProps<T>["prepareRow"];
}) => {
  const { getTableProps, headerGroups, getTableBodyProps, page, prepareRow } =
    props;

  return (
    <table {...getTableProps()} className={styles.table}>
      <thead>
        {headerGroups.map((headerGroup) => (
          // eslint-disable-next-line react/jsx-key
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              // eslint-disable-next-line react/jsx-key
              <th {...column.getHeaderProps()} aria-sort={ariaSort(column)}>
                <button type="button" {...column.getSortByToggleProps()}>
                  {column.render("Header")}
                </button>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {page.map((row: Row<T>) => {
          prepareRow(row);
          return (
            // eslint-disable-next-line react/jsx-key
            <tr {...row.getRowProps()}>
              {row.cells.map((cell) => (
                <TableCell key={cell.getCellProps().key} cell={cell} />
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

//TODO Use CSS container queries when supported across all the browsers
// https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Container_Queries
const useHideChildOnWidthExceeded = (isEnabled: boolean) => {
  const ref = React.useRef<HTMLUListElement | null>(null);
  const onResize = () => {
    if (ref.current) {
      const parentWidth = ref.current.getBoundingClientRect().width;
      const children: NodeListOf<HTMLElement> = ref.current?.querySelectorAll(
        "*[data-min-parent-width]"
      );

      children.forEach((child) => {
        if (Number(child.dataset.minParentWidth) > parentWidth) {
          child.classList.add(styles.hidden);
        } else {
          child.classList.remove(styles.hidden);
        }
      });
    }
  };

  React.useEffect(() => {
    if (isEnabled) {
      window.addEventListener("resize", onResize);

      onResize();
      return () => {
        window.removeEventListener("resize", onResize);
      };
    }
  }, [isEnabled]);

  return { ref };
};

export const TableFooter = <T extends object>(props: {
  className?: string;
  columns: Column<T>[];
  globalFilter?: string;
  setGlobalFilter?: (globalFilter: string) => void;
  pageIndex: number;
  pageCount: number;
  pageOptions: number[];
  gotoPage: (pageIndex: number) => void;
  search?: boolean;
  total?: number;
  maxResultSizeReached: boolean;
  requestResultSizeBytes?: number;
}) => {
  const { globalFilter, setGlobalFilter } = props;
  const showPagination = props.pageCount > 1;
  const showSearch = Boolean(props.search);
  const { ref } = useHideChildOnWidthExceeded(showPagination);
  const requestResultSizeInGB =
    (props.requestResultSizeBytes ?? 1073741824) / 1073741824; // Convert bytes to GB and default to 1GB if undefined
  const displaySize =
    requestResultSizeInGB < 1
      ? requestResultSizeInGB.toFixed(1)
      : Math.floor(requestResultSizeInGB);

  const onPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    props.gotoPage(parseInt(event.target.value, 10));
  };

  return (
    <ul
      ref={ref}
      className={cn(
        styles.footer,
        props.className || styles["footer-defaults"]
      )}
    >
      {typeof props.total === "number" && (
        <li data-min-parent-width={380}>
          <span className={styles.total}>
            {props.total.toLocaleString()} {props.total === 1 ? "row" : "rows"}
            {props.maxResultSizeReached && (
              <Tooltip
                style={{ maxWidth: 250, whiteSpace: "normal" }}
                position="above-center"
                label={
                  <>
                    This query result reached our {displaySize}GB maximum limit
                    and contains additional rows that can not be displayed
                  </>
                }
              >
                <div className={styles.warning}>
                  <Icon icon="warning-triangle" />
                </div>
              </Tooltip>
            )}
          </span>
        </li>
      )}
      {showSearch && (
        <li>
          <TableFooterSearch
            columns={props.columns}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </li>
      )}
      {showPagination && (
        <li>
          <button
            type="button"
            onClick={() => props.gotoPage(0)}
            disabled={props.pageIndex === 0}
          >
            <Icon icon="chevron-double-left" aria-label="First page" />
          </button>
        </li>
      )}
      {showPagination && (
        <li>
          <button
            type="button"
            onClick={() => props.gotoPage(props.pageIndex - 1)}
            disabled={props.pageIndex === 0}
          >
            <Icon icon="chevron-left" aria-label="Previous page" />
          </button>
        </li>
      )}
      {showPagination && (
        <li data-min-parent-width={520}>
          <select
            value={props.pageIndex}
            onChange={onPage}
            aria-label="Select page"
          >
            {props.pageOptions.map((i) => (
              <option key={i} value={i}>
                Page {i + 1}
              </option>
            ))}
          </select>
        </li>
      )}
      {showPagination && (
        <li>
          <button
            type="button"
            onClick={() => {
              props.gotoPage(props.pageIndex + 1);
            }}
            disabled={props.pageIndex === props.pageCount - 1}
          >
            <Icon icon="chevron-right" aria-label="Next page" />
          </button>
        </li>
      )}
      {showPagination && (
        <li>
          <button
            type="button"
            onClick={() => props.gotoPage(props.pageCount - 1)}
            disabled={props.pageIndex === props.pageCount - 1}
          >
            <Icon icon="chevron-double-right" aria-label="Last page" />
          </button>
        </li>
      )}
    </ul>
  );
};

const TableFooterSearch = <T extends object>(props: {
  columns: Column<T>[];
  globalFilter?: string;
  setGlobalFilter?: (globalFilter: string) => void;
}) => {
  const [search, setSearch] = React.useState(props.globalFilter ?? "");

  const setGlobalFilterDebounced = useAsyncDebounce((value: string) => {
    props.setGlobalFilter?.(value);
  }, 200);

  // Update the search field value and debounce the actual search.
  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilterDebounced(event.target.value);
    setSearch(event.target.value);
  };

  // Clear the search input field on ESC.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Esc" || event.key === "Escape") {
      setGlobalFilterDebounced("");
      setSearch("");
    }
  };

  return (
    <input
      aria-label="Search"
      placeholder="Search..."
      value={search || ""}
      onChange={onSearch}
      onKeyDown={onKeyDown}
    />
  );
};

const ariaSort = <T extends object>(column: HeaderGroup<T>) => {
  return column.isSorted
    ? column.isSortedDesc
      ? "descending"
      : "ascending"
    : "none";
};
